import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, List } from 'antd';
import {
  ShoppingCartOutlined,
  HeartOutlined,
  WalletOutlined,
  StarOutlined,
  FileTextOutlined,
  MessageOutlined,
  RightOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { userService, walletService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, setLoading] = useState(true);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [statsData, setStatsData] = useState({
    total_orders: 0,
    wallet_balance: 0,
    loyalty_points: 0,
    total_favorites: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, walletRes] = await Promise.all([
          userService.getDashboardStats(),
          walletService.getWallet().catch(() => null),
        ]);
        if (statsRes?.data?.data) {
          setStatsData({
            total_orders: statsRes.data.data.total_orders ?? 0,
            wallet_balance: statsRes.data.data.wallet_balance ?? 0,
            loyalty_points: statsRes.data.data.loyalty_points ?? 0,
            total_favorites: statsRes.data.data.total_favorites ?? 0,
          });
        }
        if (walletRes?.data?.balance !== undefined) {
          setStatsData((prev) => ({ ...prev, wallet_balance: walletRes.data.balance }));
        }
      } catch {
        // use defaults
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Load recently viewed products
    try {
      const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
      setRecentlyViewed(viewed.slice(0, 4));
    } catch {
      /* ignore */
    }
  }, []);

  const stats = [
    {
      title: 'Total Orders',
      value: statsData.total_orders,
      icon: <ShoppingCartOutlined />,
      color: '#2d5a3d',
      route: '/orders',
    },
    {
      title: 'Wallet Balance',
      value: statsData.wallet_balance,
      icon: <WalletOutlined />,
      color: '#2e9bd6',
      route: '/wallet',
      suffix: 'NPR',
    },
    {
      title: 'Loyalty Points',
      value: statsData.loyalty_points,
      icon: <StarOutlined />,
      color: '#f1a134',
      route: '/wallet',
    },
    {
      title: 'Wishlist',
      value: statsData.total_favorites,
      icon: <HeartOutlined />,
      color: '#52b788',
      route: '/wishlist',
    },
  ];

  const quickActions = [
    {
      title: 'Browse Products',
      desc: 'Explore our catalog',
      icon: <ShoppingCartOutlined />,
      color: '#2d5a3d',
      route: '/customer/products',
    },
    {
      title: 'View Orders',
      desc: 'Track your purchases',
      icon: <FileTextOutlined />,
      color: '#2e9bd6',
      route: '/orders',
    },
    {
      title: 'Save Favorites',
      desc: 'View your wishlist',
      icon: <HeartOutlined />,
      color: '#52b788',
      route: '/wishlist',
    },
    {
      title: 'Top Up Wallet',
      desc: 'Add funds instantly',
      icon: <WalletOutlined />,
      color: '#f1a134',
      route: '/wallet',
    },
    {
      title: 'Get Support',
      desc: 'Chat with our team',
      icon: <MessageOutlined />,
      color: '#3d7a4d',
      route: '/chat',
    },
  ];

  return (
    <div>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          marginBottom: '32px',
          padding: '28px 32px',
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #2d5a3d 0%, #3d7a4d 50%, #52b788 100%)',
          boxShadow: '0 8px 24px rgba(45, 90, 61, 0.25)',
        }}
      >
        {/* decorative brand glows */}
        <div
          style={{
            position: 'absolute',
            top: '-60px',
            right: '-40px',
            width: '220px',
            height: '220px',
            borderRadius: '50%',
            background: 'rgba(168, 230, 207, 0.18)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-80px',
            right: '90px',
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            background: 'rgba(241, 161, 52, 0.16)',
          }}
        />
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Title level={2} style={{ color: '#ffffff', margin: 0, fontWeight: 700 }}>
              Welcome back, {user?.name || user?.full_name || 'Customer'}!
            </Title>
            <Paragraph
              style={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.85)', margin: '6px 0 0' }}
            >
              Here's what's happening with your account today.
            </Paragraph>
          </div>
        </div>
      </div>

      <Row gutter={[20, 20]} style={{ marginBottom: '32px' }}>
        {stats.map((stat, i) => (
          <Col xs={24} sm={12} md={6} key={i}>
            <Card
              hoverable
              onClick={() => navigate(stat.route)}
              style={{
                borderRadius: '16px',
                border: '1px solid #e8f0e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              styles={{
                body: { padding: '24px' },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Statistic
                  title={<span style={{ color: '#6b7280', fontSize: '13px' }}>{stat.title}</span>}
                  value={stat.value}
                  precision={0}
                  suffix={
                    stat.suffix ? (
                      <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
                        {stat.suffix}
                      </span>
                    ) : undefined
                  }
                  valueStyle={{ color: '#2d5a3d', fontSize: '26px', fontWeight: 700 }}
                />
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '14px',
                    background: stat.color + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card
            title={<span style={{ color: '#2d5a3d', fontWeight: 600 }}>Quick Actions</span>}
            style={{
              borderRadius: '16px',
              border: '1px solid #e8f0e8',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <Row gutter={[16, 16]}>
              {quickActions.map((action, i) => (
                <Col xs={24} sm={12} md={8} key={i}>
                  <Card
                    hoverable
                    onClick={() => navigate(action.route)}
                    style={{
                      borderRadius: '12px',
                      border: '1px solid #f0f5f0',
                      background: '#fafff5',
                      transition: 'all 0.3s ease',
                    }}
                    styles={{ body: { padding: '16px' } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: action.color + '15',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          color: action.color,
                          flexShrink: 0,
                        }}
                      >
                        {action.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ color: '#2d5a3d', display: 'block' }}>
                          {action.title}
                        </Text>
                        <Text style={{ color: '#9ca3af', fontSize: '12px' }}>{action.desc}</Text>
                      </div>
                      <RightOutlined style={{ color: '#d1d5db', fontSize: '12px' }} />
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        {recentlyViewed.length > 0 && (
          <Col xs={24}>
            <Card
              title={
                <span style={{ color: '#2d5a3d', fontWeight: 600 }}>
                  <EyeOutlined style={{ marginRight: 8 }} />
                  Recently Viewed
                </span>
              }
              style={{
                borderRadius: 16,
                border: '1px solid #e8f0e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 4 }}
                dataSource={recentlyViewed}
                renderItem={(item) => (
                  <List.Item>
                    <Card
                      hoverable
                      size="small"
                      onClick={() => navigate(`/customer/products/${item.id}`)}
                      styles={{ body: { padding: 12 } }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: 8,
                            background: '#f0faf0',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 18,
                            color: '#52b788',
                          }}
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
                            />
                          ) : (
                            <ShoppingCartOutlined />
                          )}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <Text
                            strong
                            style={{
                              fontSize: 13,
                              color: '#2d5a3d',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.name}
                          </Text>
                          <Text style={{ fontSize: 12, color: '#52b788' }}>
                            NPR {Number(item.price || 0).toLocaleString()}
                          </Text>
                        </div>
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Dashboard;
